// Les quatre rubriques identitaires que la fiche boutique ne portait pas
// (NF525 R13 en-tête 10, 11, 14, 15 — X en D, A, N, T et JP). Elles complètent
// `publicName`, `address`, `countryCode` et `siret`, déjà présents : ensemble,
// ce sont les huit champs que `resolveIssuer` (caisse) hisse dans le registre.
export const CHAMPS_IDENTITE_FISCALE = [
  { name: 'zipCode', label: 'Code postal' },
  { name: 'city', label: 'Ville' },
  { name: 'naf', label: 'Code NAF / APE' },
  { name: 'vatNumber', label: 'N° de TVA intracommunautaire' },
] as const;

export type ChampFiscal = (typeof CHAMPS_IDENTITE_FISCALE)[number]['name'];

// Le SIRET a déjà son entrée dans « Informations générales » : ce n'est pas une
// rubrique du bloc, mais sa FORME se contrôle ici avec les autres — c'est la
// rubrique 13 de l'en-tête R13, et un SIRET à treize chiffres part dans
// l'export aussi sûrement qu'un NAF absent (même liste que la garde serveur,
// `apps/engine/apps/shop/src/services/identite-fiscale.ts`).
export type ChampControle = ChampFiscal | 'siret';

type FicheFiscale = Partial<Record<ChampControle, string | undefined>>;

// Formats officiels. NAF/APE : quatre chiffres et une lettre majuscule
// (nomenclature INSEE, ex. 5610A « restauration traditionnelle »). TVA
// intracommunautaire française : « FR », deux caractères de clé et les neuf
// chiffres du SIREN — soit onze chiffres pour une clé numérique, la forme que
// l'administration attend dans l'export.
// SIRET : quatorze chiffres (`SIRET_PATTERN`, `packages/fiscal`).
const FORMATS_FISCAUX: Partial<Record<ChampControle, RegExp>> = {
  siret: /^\d{14}$/,
  naf: /^\d{4}[A-Z]$/,
  vatNumber: /^FR\d{11}$/,
};

// L'ordre du formulaire : le SIRET est saisi au-dessus du bloc fiscal.
const CHAMPS_CONTROLES: ChampControle[] = [
  'siret',
  ...CHAMPS_IDENTITE_FISCALE.map(({ name }) => name),
];

const LIBELLE_SIRET = 'SIRET';

const lire = (shop: FicheFiscale | undefined, name: ChampControle): string =>
  (shop?.[name] ?? '').trim();

// Les espaces INTÉRIEURS sont retirés avant contrôle de forme, comme côté
// serveur : un SIRET se recopie « 892 719 220 00022 » sur un Kbis, une TVA
// « FR 12 345 678 901 » sur un en-tête de facture. La présence, elle, se juge
// au simple rognage — une ville a le droit d'avoir des espaces.
const compacter = (brut: string): string => brut.replace(/\s+/g, '').toUpperCase();

export function champsFiscauxManquants(shop: FicheFiscale | undefined): ChampFiscal[] {
  return CHAMPS_IDENTITE_FISCALE.filter(({ name }) => lire(shop, name) === '').map(
    ({ name }) => name,
  );
}

// Un NAF ou une TVA mal formés valent une absence pour l'administration :
// l'export les recopie tels quels. On les signale à la saisie, pas à l'audit.
// La casse est normalisée avant contrôle : le clavier d'un administrateur
// produit volontiers « 5610a » ou « fr… », que la fiche corrige d'elle-même
// côté serveur — refuser la saisie pour ça ne rendrait service à personne.
// Un champ vide relève de `champsFiscauxManquants`, jamais d'ici.
export function champsFiscauxMalFormes(shop: FicheFiscale | undefined): ChampControle[] {
  return CHAMPS_CONTROLES.filter((name) => {
    const format = FORMATS_FISCAUX[name];
    const brut = lire(shop, name);

    return !!format && brut !== '' && !format.test(compacter(brut));
  });
}

export function libelleFiscal(name: ChampControle): string {
  return CHAMPS_IDENTITE_FISCALE.find((champ) => champ.name === name)?.label ?? LIBELLE_SIRET;
}

// Une caisse au sens NF525 = un registre fiscal enregistré au centralisateur
// pour cette boutique. La liste vient de `readFiscalRegisters` ; tant qu'elle
// n'est pas chargée, on n'exige rien (bloquer sur une donnée absente
// empêcherait de corriger la fiche, ce qui est exactement l'inverse du but).
export function boutiquePorteUneCaisse(
  registres: readonly { shopId: string }[] | undefined,
  shopId: string | undefined,
): boolean {
  if (registres === undefined || shopId === undefined) return false;

  return registres.some((registre) => registre.shopId === shopId);
}
